import React from "react";
import PropTypes from "prop-types";
import { Outlet, Link } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  Menu,
  MenuItem,
  Select,
  Typography,
  IconButton,
  Chip,
  Tooltip,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import MenuIcon from "@mui/icons-material/Menu";
import FormControl from "@mui/material/FormControl";
import moment from "moment";

import AppView from "layouts/AppView";
import LanguageSelect from "components/LanguageSelect";
import i18n from "i18";
import _helper from "actions/_helper";
import _request from "actions/_request";
import { isLowBalance, planIncludesAI } from "utils/aiCredits";
import TeamIcon from "images/team_icon.svg";

import "./style.css";

import ThemeSwitcher from "../../ThemeSwitcher";
import Sidebar from "../../components/SideBarMenu/Sidebar";
import AppContext from "../../AppContext";
import Onboarding from "../../components/Onboarding";
import SupportButton from "../../components/SupportButton";
import { clearReturnTo } from "../../utils/returnTo";

const paperPropsSx = {
  "width": 314,
  "overflow": "visible",
  "filter": "none",
  "mt": "0px",
  "background": "var(--hedear-bg)",
  "& .MuiAvatar-root": {
    width: 32,
    height: 32,
    ml: -0.5,
    mr: 1,
  },
};

export default class AppLayout extends AppView {
  static contextType = AppContext;

  static propTypes = {
    navigate: PropTypes.func,
  };

  static defaultProps = {
    navigate: null,
  };

  state = {
    profileMenuOpened: null,
    project: 10,
    companyType: "invited",
    sidebarExpanded: false,
  };

  componentDidMount() {
    this.context.loadMetaData();
  }

  handleChange = (event) => {
    const project = event.target.value;
    const companyType = project === 10 ? "invited" : "owner";

    this.setState({ project, companyType });
  };

  logoutConfirm = () => {
    _helper.confirm("dialog.logout", this.logoutSend);
  };

  logoutSend = async () => {
    _helper.startLoading();
    const resp = await _request.auth.logout();
    _helper.endLoading();

    if (!resp?.isOk) return;

    // Signing out on purpose is not an interrupted journey — drop any pending
    // destination so the next login lands on the dashboard as expected.
    clearReturnTo();
    this.logout();
  };

  logout = () => {
    _helper.cookie("XSRF-TOKEN", "", -1);
    this.props.navigate("/auth/login");
  };

  setProfileMenuOpened = (profileMenuOpened) => {
    this.setState({ profileMenuOpened });
  };

  handleProfileMenuOpen = ({ currentTarget }) => {
    this.setProfileMenuOpened(currentTarget);
  };

  handleProfileMenuClose = () => {
    this.setProfileMenuOpened(null);
  };

  handleLogout = () => {
    this.handleProfileMenuClose();
    this.logoutConfirm();
  };

  toggleSidebar = () => {
    this.setState((s) => ({ sidebarExpanded: !s.sidebarExpanded }));
  };

  insertProfile({ full_name, email, id }) {
    let imageSrc = null;
    if (id) {
      imageSrc = _request.accounts.formLink(id, 120);
    }

    // Get initials for placeholder
    const getInitials = (name) => {
      if (!name) return "U";
      return name
        .split(" ")
        .map((word) => word.charAt(0))
        .join("")
        .toUpperCase()
        .substring(0, 2);
    };

    return (
      <Button
        variant="text"
        color="inherit"
        className="profileButton"
        onClick={this.handleProfileMenuOpen}>
        <Box className="flex gap8 centerV">
          <Box className="flex column gap4 items-center" sx={{ display: { xs: "none", md: "flex" } }}>
            <Typography
              sx={{
                fontWeight: "bold",
                fontSize: 15,
                textTransform: "capitalize",
                color: "var(--text-color)",
              }}>
              {full_name}
            </Typography>
            <Typography
              sx={{
                fontSize: 12,
                textTransform: "none",
                color: "var(--header-email-color)",
              }}>
              {email}
            </Typography>
          </Box>
          <Avatar sx={{ backgroundColor: "var(--text-color)" }}>
            {imageSrc ? (
              <img
                src={imageSrc}
                alt={full_name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  // If image fails to load, hide it and show initials
                  e.target.style.display = "none";
                  const parent = e.target.parentElement;
                  const initialsEl = parent.querySelector(".avatar-initials");
                  if (initialsEl) {
                    initialsEl.style.display = "flex";
                  }
                }}
              />
            ) : null}
            <Typography
              className="avatar-initials"
              sx={{
                color: "white",
                fontWeight: "bold",
                fontSize: 16,
                display: imageSrc ? "none" : "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                height: "100%",
                position: imageSrc ? "absolute" : "static",
                top: 0,
                left: 0,
              }}>
              {getInitials(full_name)}
            </Typography>
          </Avatar>
        </Box>
      </Button>
    );
  }

  insertCreditsChip(ai_credits, planHasNoAI) {
    const monthly_remaining = ai_credits?.monthly_remaining ?? 0;
    const monthly_allowance = ai_credits?.monthly_allowance ?? 0;
    const isLow = isLowBalance(ai_credits);

    // On a plan without AI, a balance here reads as spendable when it is not:
    // every AI entry point refuses with "not available in your plan". Point at
    // the upgrade instead of a number the user cannot use.
    if (planHasNoAI) {
      return (
        <Tooltip title={i18n.t("aiCredits.noCreditsInPlan")} arrow>
          <Chip
            component={Link}
            to="/app/pricing"
            icon={<AutoAwesomeIcon sx={{ fontSize: "14px !important" }} />}
            label={i18n.t("aiCredits.upgradePlan")}
            size="small"
            clickable
            sx={{
              backgroundColor: "rgba(150,150,150,0.12)",
              color: "var(--text-color)",
              border: "1px solid rgba(150,150,150,0.35)",
              fontWeight: 600,
              fontSize: 12,
              opacity: 0.85,
              "& .MuiChip-icon": { color: "var(--text-color)", opacity: 0.7 },
              textDecoration: "none",
            }}
          />
        </Tooltip>
      );
    }

    return (
      <Tooltip title={i18n.t("aiCredits.tooltipHeader")} arrow>
        <Chip
          component={Link}
          to="/app/credits"
          icon={<AutoAwesomeIcon sx={{ fontSize: "14px !important" }} />}
          label={`${monthly_remaining} / ${monthly_allowance}`}
          size="small"
          clickable
          sx={{
            backgroundColor: isLow ? "#FE848420" : "#42C0C720",
            color: isLow ? "#FE8484" : "#42C0C7",
            border: `1px solid ${isLow ? "#FE848460" : "#42C0C760"}`,
            fontWeight: 600,
            fontSize: 12,
            "& .MuiChip-icon": { color: isLow ? "#FE8484" : "#42C0C7" },
            textDecoration: "none",
          }}
        />
      </Tooltip>
    );
  }

  insertHeader(data) {
    const { user, user_team, ai_credits } = data;
    // Same rule the AI dialogs use: judge the plan, not the current balance.
    const currentPlan = data?.subscription?.package;
    const planHasNoAI = !!currentPlan && !planIncludesAI(currentPlan);
    const { project } = this.state;
    const personalCompanyName = user?.company_name;
    const invitedTeamName =
      user_team?.company_name || user_team?.name || user_team?.id;

    return (
      <Box className="flex gap24 centerV">
        <Box
          className="flex centerV gap8 font f14 lh border-none"
          sx={{ minWidth: "max-content", display: { xs: "none", md: "flex" } }}>
          <FormControl fullWidth>
            <Select
              labelId="project-select-label"
              value={project}
              onChange={this.handleChange}
              className="project-select f14 lh"
              variant="outlined"
              sx={{
                minWidth: 160,
                backgroundColor: "var(--background-menu-list)",
                color: "#9FA3BD",
                margin: "0 12px",
              }}
              renderValue={(value) => (
                <Box className="flex centerV gap8">
                  <img
                    src={TeamIcon}
                    alt="Team Icon"
                    style={{
                      width: 20,
                      height: 20,
                    }}
                  />
                  <Typography
                    sx={{
                      color: "#BAC0FF",
                      fontWeight: 600,
                      marginLeft: 2,
                      marginRight: 2,
                    }}>
                    {value === 10
                      ? personalCompanyName || "My Company"
                      : invitedTeamName}
                  </Typography>
                </Box>
              )}>
              <MenuItem value={10}>
                {personalCompanyName || "My Company"}
              </MenuItem>
              {invitedTeamName && (
                <MenuItem value={20}>{invitedTeamName}</MenuItem>
              )}
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ display: { xs: "none", sm: "block" } }}>{this.insertCreditsChip(ai_credits, planHasNoAI)}</Box>
        {this.insertProfile(user || {})}
        {this.insertProfilePopup(data)}
      </Box>
    );
  }

  insertProfilePopup(data) {
    const { profileMenuOpened } = this.state;
    const { userPackage, userInfo } = data;
    const date = userInfo?.expire_date;
    let formattedDate;

    if (userPackage?.id === 8) {
      formattedDate = "Expired";
    } else if (userPackage?.id === 9) {
      formattedDate = "Paused";
    } else if (userPackage?.id === 1) {
      // Free Version - no billing date
      formattedDate = null;
    } else if (date && moment(date).isValid()) {
      formattedDate = moment(date).format(userInfo?.date_format);
    } else {
      formattedDate = "Invalid date";
    }

    const open = !!profileMenuOpened;

    return (
      <Menu
        anchorEl={profileMenuOpened}
        id="account-menu"
        open={open}
        onClose={this.handleProfileMenuClose}
        onClick={this.handleProfileMenuClose}
        PaperProps={{
          elevation: 0,
          sx: paperPropsSx,
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}>
        <MenuItem
          component={Link}
          onClick={this.handleProfileMenuClose}
          to="/app/profile"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}>
          <Typography
            sx={{ color: "var(--text-color)", fontSize: 14, fontWeight: 500 }}>
            {i18n.t("pages.profile")}
          </Typography>
        </MenuItem>
        <MenuItem
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}>
          <Typography
            sx={{ color: "var(--text-color)", fontSize: 14, fontWeight: 500 }}>
            {i18n.t("pages.theme")}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <ThemeSwitcher />
          </Box>
        </MenuItem>
        <MenuItem
          component={Link}
          to="/app/pricing"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}>
          <Box>
            <Typography
              sx={{
                color: "var(--text-color)",
                fontSize: 14,
                fontWeight: 500,
              }}>
              {i18n.t("pages.changePlan")}
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ color: "#49B1B5", fontSize: 14 }}>
              {userPackage?.title}
            </Typography>
          </Box>
        </MenuItem>
        {/* Only show Next Billing for paid plans */}
        {userPackage?.id !== 1 && (
          <MenuItem
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}>
            <Box>
              <Typography
                sx={{
                  color: "var(--text-color)",
                  fontSize: 14,
                  fontWeight: 500,
                }}>
                {i18n.t("pages.nextBilling")}
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ color: "#49B1B5", fontSize: 14 }}>
                {formattedDate}
              </Typography>
            </Box>
          </MenuItem>
        )}
        <MenuItem
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}>
          <Box>
            <Typography
              sx={{
                color: "var(--text-color)",
                fontSize: 14,
                fontWeight: 500,
              }}>
              {i18n.t("pages.settings")}
            </Typography>
          </Box>
          <Box>
            <LanguageSelect />
          </Box>
        </MenuItem>
        <MenuItem onClick={this.handleLogout}>
          <Typography sx={{ color: "#FE8484", fontSize: 14, fontWeight: 500 }}>
            {i18n.t("pages.logout")}
          </Typography>
        </MenuItem>
      </Menu>
    );
  }

  renderView() {
    const { metaData, wasLoaded } = this.context;
    const { companyType } = this.state;

    return (
      <>
        <Box className="layout flex1 flex">
          <Sidebar expanded={this.state.sidebarExpanded} />
          <Box className="rightSide flex1 flex column">
            <Box className="header flex between centerV">
              <Box className="flex centerV gap16">
                <IconButton
                  aria-label="toggle sidebar"
                  onClick={this.toggleSidebar}
                  className="burger"
                  size="large">
                  <MenuIcon />
                </IconButton>
                <Box className="newLogo" />
              </Box>
              <Box className="flex centerV gap16">
                <span id="onboarding-slot" />
                {wasLoaded && this.insertHeader(metaData)}
              </Box>
            </Box>
            <Box
              className="pageZone pos rel flex1 scrolling"
              sx={{ padding: { xs: "12px", sm: "16px", md: "25px" } }}>
              <Box className="pageZoneInner flex">
                {wasLoaded && <Outlet context={{ metaData, companyType }} />}
              </Box>
            </Box>
          </Box>
        </Box>
        {wasLoaded && <Onboarding />}
        <SupportButton />
      </>
    );
  }
}
